import { useMemo } from 'react';

import { SessionSchema } from '@eluve/api-contract';

import { useProvidedSessionContext } from './SessionContextProvider';
import { useTenantScopedSessionData } from './TenantScopedSessionProvider';

/**
 * Provides access to the parsed session data for the currently
 * authenticated user. If the session is not available, an error will be thrown
 *
 * Under a tenant route the tab's MINTED tenant-scoped token owns the
 * tenant-specific Hasura claims (ENG-5545): they match what the tab's GraphQL
 * requests actually carry, whereas the shared session's tenant/role claims
 * freeze at their login-time tenant. Everything else stays LIVE from the
 * shared session — fields like `impersonation` and `allowedTenantIds` mutate
 * on the shared payload (renewals, refreshAllowedTenants) between re-mints,
 * and a minted snapshot of them can be up to a token-lifetime stale.
 */
export const useSession = () => {
  const tenantScopedSession = useTenantScopedSessionData();
  const session = useProvidedSessionContext();

  if (session.loading || !session.doesSessionExist) {
    throw new Error('Attempted to access session but it is not available');
  }

  const parsedSession = SessionSchema.parse(session.accessTokenPayload);

  if (tenantScopedSession) {
    return {
      ...parsedSession,
      'https://hasura.io/jwt/claims':
        tenantScopedSession['https://hasura.io/jwt/claims'],
      // Tenant-scoped like the claims: derived from external_users for the
      // SELECTED tenant, so the shared A cookie's value would be wrong (or
      // missing) on a B tab.
      externalUserId: tenantScopedSession.externalUserId,
    };
  }

  return parsedSession;
};

export const useUserIdFromSession = () => {
  const session = useSession();
  return session['https://hasura.io/jwt/claims']['x-hasura-user-id'];
};

/**
 * We should only be using this function whenever we do not know which tenant user is part of.
 * In most cases, we should use tenantID from params
 */
export const useTenantIdFromSession = () => {
  const session = useSession();
  return session['https://hasura.io/jwt/claims']['x-hasura-tenant-id'];
};

export const useRolesFromSession = () => {
  const session = useSession();
  return session['https://hasura.io/jwt/claims']['x-hasura-allowed-roles'];
};

export const useIsTenantAdminFromSession = () => {
  const roles = useRolesFromSession();
  return roles.includes('tenant-admin');
};

export const useUserDisplayFromSession = () => {
  const session = useSession();

  let userInitials = `${session.firstName?.[0] ?? ''}${
    session.lastName?.[0] ?? ''
  }`;
  // fallback when user does not have first and last name in the system
  if (userInitials.length === 0 && session.email) {
    // assumes that no one will have an email address with less than 2 chars
    userInitials = session.email.slice(0, 2) as string;
  }
  return {
    email: session.email,
    userInitials: userInitials.toUpperCase(),
    firstName: session.firstName,
    lastName: session.lastName,
  };
};

export const useIsEluveAdmin = () => {
  const session = useSession();
  return session.isEluveAdmin;
};

export const useIsMlEngineer = () => {
  const session = useSession();
  return session.isMlEngineer;
};

/**
 * Returns the Apollo `context` override for review-dash queries so they run
 * under `ml-engineer` when the user has that role (preferred, since it's the
 * superset) and fall back to `reviewer` otherwise. Callers shouldn't hardcode
 * `reviewerHasuraContext` on review-dash operations — pure ml-engineer users
 * don't have `reviewer` in their allowed roles and Hasura will reject.
 */
export const useReviewHasuraContext = () => {
  const isMlEngineer = useIsMlEngineer();
  return useMemo(
    () => ({
      headers: {
        'x-hasura-role': isMlEngineer ? 'ml-engineer' : 'reviewer',
      },
    }),
    [isMlEngineer],
  );
};

export const useIsPromptTemplateCreator = () => {
  const session = useSession();
  return session.isPromptTemplateCreator;
};

export const useIsTemplatePublisher = () => {
  const session = useSession();
  return session.isTemplatePublisher;
};
